import { API_URL } from 'utils/env';
import { Platform } from 'react-native';
import { getHash } from './multihash';
import { getToken } from './storage';

let RNFetchBlob: any;

if (process.env.WEB !== 'true' && Platform.OS !== 'ios') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  RNFetchBlob = require('rn-fetch-blob').default;
}

interface PresignedPost {
  fields: any;
  url: string;
  S3_BUCKET: string;
}

export async function uploadFile(
  imgData: string | ArrayBuffer,
  fileType: string,
  uri?: string
): Promise<string> {
  const signedPutUrl = API_URL + '/api/s3/sign';

  const [file, name] = await processFile(imgData, fileType, uri);

  const token = await getToken();
  const res = await fetch(signedPutUrl + `?file_name=${name}`, {
    credentials: 'include',
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const signedRequest = await res.json();
  if (!signedRequest) throw new Error('Could not get sigend image upload request.');
  return await uploadToS3(file, signedRequest);
}

async function uploadToS3(file: Blob, signedRequest: PresignedPost): Promise<string> {
  try {
    if (Platform.OS === 'android') RNFetchBlob.session('uploads').dispose();
    const { S3_BUCKET, fields, url } = signedRequest;

    const form = new FormData();
    form.append('Content-Type', file.type);
    Object.entries(fields).forEach(([k, v]) => {
      form.append(k, v as string | Blob);
    });
    form.append('file', file);

    const options = {
      method: 'POST',
      Accept: 'application/json, application/xml, text/plain, text/html, *.*',
      'Content-Type': 'multipart/form-data',
      body: form,
    };
    const res = await fetch(url, options);
    if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
    return `https://${S3_BUCKET}.s3.amazonaws.com/${fields.Key}`;

    // if (Platform.OS === 'android') {
    //   if (res.status === 201) {
    //     return { success: true, url: publicUrl };
    //   }
    //   return { success: false, res };
    // }
  } catch (err) {
    if (Platform.OS === 'android') RNFetchBlob.session('uploads').dispose();
    throw err;
  }
}

const processFile = async (
  uri: string | ArrayBuffer | any,
  type: string,
  localUri: string | undefined
): Promise<[Blob | any, string]> => {
  if (
    Platform.OS === 'android' &&
    typeof localUri === 'string' &&
    (localUri.match('http://') || localUri.match('https://'))
  ) {
    const res = await RNFetchBlob.config({
      fileCache: true,
      session: 'uploads',
    }).fetch('GET', localUri);
    localUri = 'file://' + res.path();
  }

  // TODO the whole point of this is to get ipfs-style hashes for file names
  // this can be refactored
  if (uri instanceof ArrayBuffer) {
    const fileBuffer = Buffer.from(uri);
    const hash = await getHash(fileBuffer);
    const name = `${hash}.${type?.split('/')?.[1] || ''}`;
    const file = new Blob([uri], { type });
    return [file, name];
  } else if (isDataURL(uri)) {
    const [_file, fileBuffer] = dataURItoBlob(uri);
    const hash = await getHash(fileBuffer as Buffer);
    const name = `${hash}.${type?.split('/')?.[1] || ''}`;

    // This allows uploads from react-native
    // otherwise blog doesn't work so well
    const file = localUri
      ? {
          uri: localUri,
          name,
          type,
        }
      : _file;
    return [file, name];
  }
  if (typeof uri === 'string') {
    const hash = await getHash(Buffer.from(uri, 'utf8'));
    const type = ext(uri);
    const name = hash + '.' + type;
    const file = {
      uri,
      name,
      type: 'image/' + type,
    };
    return [file, name];
  }
  throw new Error('Error processing file');
};

function dataURItoBlob(dataURI: string): [Blob, Buffer] {
  // convert base64/URLEncoded data component to raw binary data held in a string
  let byteString;
  if (dataURI.split(',')[0].indexOf('base64') >= 0) {
    byteString = atob(dataURI.split(',')[1]);
  } else {
    byteString = unescape(dataURI.split(',')[1]);
  }

  // separate out the mime component
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

  // write the bytes of the string to a typed array
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  const fileBuffer = Buffer.from(byteString);
  const file = new Blob([ia], { type: mimeString });
  return [file, fileBuffer];
}

// eslint-disable-next-line
const DATA_URL_REGEX = /^\s*data:([a-z]+\/[a-z]+(;[a-z\-]+\=[a-z\-]+)?)?(;base64)?,[a-z0-9\!\$\&\'\,\(\)\*\+\,\;\=\-\.\_\~\:\@\/\?\%\s]*\s*$/i;

function isDataURL(s: string) {
  return s?.match && s.match(DATA_URL_REGEX);
}

function ext(url: string) {
  return (url = url.substr(1 + url.lastIndexOf('/')).split('?')[0])
    .split('#')[0]
    .substr(url.lastIndexOf('.') + 1);
}

export async function getBase64ImageFromUrl(
  imageUrl: string
): Promise<string | ArrayBuffer | null> {
  const res = await fetch(imageUrl);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(blob);
  });
}
